import { motion } from "framer-motion";
import { Linkedin, Mail, Globe, GraduationCap, Star, Clock, ArrowUpRight, BookOpen } from "lucide-react";
import { resolveTrainerImage, trainerInitials, trainerImageStyle } from "@/lib/trainers";

/**
 * Reusable, CMS-driven trainer/member card.
 *
 * Props:
 *  - trainer: member object from /api/cms/members
 *  - variant: "default" | "compact" | "spotlight"
 *  - index:   for staggered entrance animation
 *  - showContact: render an email CTA button
 */
export default function TrainerCard({ trainer, variant = "default", index = 0, showContact = false, onViewProfile }) {
  if (!trainer) return null;
  const t = trainer;
  const photo = resolveTrainerImage(t.photo);

  const item = {
    hidden: { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, delay: Math.min(index * 0.06, 0.4), ease: [0.16, 1, 0.3, 1] },
    },
  };

  if (variant === "compact") {
    return (
      <motion.div
        variants={item}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-40px" }}
        whileHover={{ y: -3 }}
        className="group min-w-0 rounded-2xl bg-white border border-gray-200 p-5 hover:shadow-md hover:border-gray-300 transition-all"
        data-testid={`trainer-card-${t.id}`}
      >
        <div className="flex items-center gap-4 min-w-0">
          <TrainerAvatar photo={photo} name={t.name} size={56} trainer={t} />
          <div className="flex-1 min-w-0">
            <div className="font-display text-base font-semibold text-[#0F172A] leading-tight truncate">
              {t.name}
              {t.featured && <FeaturedDot />}
            </div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">
              {[t.designation, t.department].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
        {t.bio && <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-2 break-words">{t.bio}</p>}
      </motion.div>
    );
  }

  if (variant === "spotlight") {
    return (
      <motion.div
        variants={item}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="grid md:grid-cols-[280px,1fr] gap-6 md:gap-10 items-start rounded-3xl bg-white border border-gray-200 p-6 md:p-10 shadow-xl shadow-gray-900/[0.04]"
        data-testid={`trainer-card-${t.id}`}
      >
        <div className="overflow-hidden rounded-2xl bg-gray-100 aspect-square">
          <TrainerHeroImage photo={photo} name={t.name} trainer={t} />
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {t.featured && <FeaturedBadge />}
            {t.experience && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500">
                <Clock className="w-3 h-3" /> {t.experience}
              </span>
            )}
          </div>
          <h3 className="mt-3 font-display text-3xl font-semibold text-[#0F172A] leading-tight">{t.name}</h3>
          <div className="mt-1 text-sm text-gray-500">
            {[t.designation, t.department, t.specialization].filter(Boolean).join(" · ")}
          </div>
          {t.bio && <p className="mt-5 text-gray-700 leading-relaxed">{t.bio}</p>}
          <QualPills quals={t.qualifications} />
          <TrainerActions email={t.email} linkedin={t.linkedin} showContact={showContact} />
        </div>
      </motion.div>
    );
  }

  // default variant — the workhorse used on Home / About / Programs
  return (
    <motion.article
      variants={item}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-40px" }}
      whileHover={{ y: -4 }}
      className="group flex h-full flex-col rounded-2xl overflow-hidden bg-white border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all"
      data-testid={`trainer-card-${t.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <TrainerHeroImage photo={photo} name={t.name} trainer={t} />
        {t.featured && (
          <div className="absolute top-3 left-3">
            <FeaturedBadge />
          </div>
        )}
        {t.experience && (
          <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[10px] font-semibold text-white">
            <Clock className="w-3 h-3" /> {t.experience}
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="min-h-[48px]">
          <h3 className="font-display text-lg font-semibold leading-snug text-[#0F172A]">{t.name}</h3>
          <div className="text-xs text-gray-500 mt-0.5">
            {[t.designation, t.department].filter(Boolean).join(" · ")}
          </div>
        </div>
        {t.specialization && (
          <div className="mt-3 inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[10px] font-bold uppercase tracking-wider">
            {t.specialization}
          </div>
        )}
        {t.bio && <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-3">{t.bio}</p>}
        {t.subjects?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {t.subjects.slice(0, 4).map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-[10px] font-medium text-gray-600">
                <BookOpen className="w-2.5 h-2.5" /> {s}
              </span>
            ))}
          </div>
        )}
        <QualPills quals={t.qualifications} compact />
        <div className="mt-auto pt-4 flex items-center justify-between border-t border-gray-100">
          <div className="flex items-center gap-2">
            {t.linkedin && (
              <a
                href={t.linkedin}
                target="_blank"
                rel="noreferrer"
                data-testid={`trainer-linkedin-${t.id}`}
                className="w-8 h-8 rounded-full grid place-items-center bg-gray-50 border border-gray-100 text-gray-600 hover:text-[#4F46E5] hover:border-indigo-200 transition-colors"
                aria-label={`${t.name} on LinkedIn`}
              >
                <Linkedin className="w-3.5 h-3.5" />
              </a>
            )}
            {t.website && (
              <a
                href={t.website}
                target="_blank"
                rel="noreferrer"
                data-testid={`trainer-website-${t.id}`}
                className="w-8 h-8 rounded-full grid place-items-center bg-gray-50 border border-gray-100 text-gray-600 hover:text-[#4F46E5] hover:border-indigo-200 transition-colors"
                aria-label={`${t.name}'s website`}
              >
                <Globe className="w-3.5 h-3.5" />
              </a>
            )}
            {t.email && (
              <a
                href={`mailto:${t.email}`}
                data-testid={`trainer-email-${t.id}`}
                className="w-8 h-8 rounded-full grid place-items-center bg-gray-50 border border-gray-100 text-gray-600 hover:text-[#4F46E5] hover:border-indigo-200 transition-colors"
                aria-label={`Email ${t.name}`}
              >
                <Mail className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
          {onViewProfile ? (
            <button
              onClick={() => onViewProfile(t)}
              data-testid={`trainer-view-profile-${t.id}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#4F46E5] hover:underline"
            >
              View Profile <ArrowUpRight className="w-3 h-3" />
            </button>
          ) : (
            showContact && t.email && (
              <a
                href={`mailto:${t.email}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#4F46E5] hover:underline"
              >
                Contact <ArrowUpRight className="w-3 h-3" />
              </a>
            )
          )}
        </div>
      </div>
    </motion.article>
  );
}

function TrainerHeroImage({ photo, name, trainer }) {
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        loading="lazy"
        decoding="async"
        className="w-full h-full group-hover:scale-[1.05] transition-transform duration-700"
        style={trainerImageStyle(trainer)}
        onError={(e) => {
          e.currentTarget.style.display = "none";
          e.currentTarget.parentElement.dataset.fallback = "1";
        }}
      />
    );
  }
  return (
    <div className="w-full h-full bg-gradient-to-br from-indigo-100 via-white to-emerald-100 grid place-items-center">
      <div className="font-display text-5xl font-semibold text-[#0F172A]/40">
        {trainerInitials(name)}
      </div>
    </div>
  );
}

function TrainerAvatar({ photo, name, size = 48, trainer }) {
  const s = { width: size, height: size };
  if (photo) {
    // Fixed-size, overflow-hidden wrapper so a CMS zoom/position transform on the image
    // (trainerImageStyle can add `transform: scale(...)`) is clipped to the circle instead
    // of bleeding outside it — a transform on the img itself isn't clipped by its own
    // overflow, only by an ancestor's.
    return (
      <div className="rounded-full ring-1 ring-gray-200 shrink-0 overflow-hidden" style={s}>
        <img
          src={photo}
          alt={name}
          loading="lazy"
          className="w-full h-full"
          style={trainerImageStyle(trainer)}
        />
      </div>
    );
  }
  return (
    <div
      className="rounded-full grid place-items-center bg-gradient-to-br from-indigo-400 to-emerald-400 text-white font-semibold shrink-0"
      style={{ ...s, fontSize: Math.max(12, size * 0.32) }}
    >
      {trainerInitials(name)}
    </div>
  );
}

function FeaturedBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider border border-amber-200">
      <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Featured
    </span>
  );
}

function FeaturedDot() {
  return (
    <span
      className="inline-block ml-2 w-2 h-2 rounded-full bg-amber-400 align-middle"
      aria-label="Featured"
    />
  );
}

function QualPills({ quals, compact = false }) {
  if (!quals || !quals.length) return null;
  const shown = compact ? quals.slice(0, 2) : quals;
  const rest = compact ? Math.max(0, quals.length - 2) : 0;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {shown.map((q, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[10px] font-medium border border-indigo-100"
        >
          <GraduationCap className="w-3 h-3" /> {q}
        </span>
      ))}
      {rest > 0 && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 text-[10px] font-medium border border-gray-200">
          +{rest} more
        </span>
      )}
    </div>
  );
}

function TrainerActions({ email, linkedin, showContact }) {
  if (!email && !linkedin && !showContact) return null;
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3">
      {linkedin && (
        <a
          href={linkedin}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5] hover:underline"
        >
          <Linkedin className="w-4 h-4" /> LinkedIn
        </a>
      )}
      {email && showContact && (
        <a
          href={`mailto:${email}`}
          className="inline-flex items-center gap-1.5 bg-[#050914] hover:bg-[#0F172A] text-white text-sm font-semibold px-4 py-2 rounded-full transition-all"
        >
          <Mail className="w-4 h-4" /> Get in touch
        </a>
      )}
    </div>
  );
}
